import { Task, TaskStatus } from '@/types';
import { isAdminOrManager, normalizeRole } from '@/shared/utils/roles';
import { TaskContextType, TaskTablePermissions } from './types';
import { hasLoggedTimeBlockingDelete } from './utils';

export interface User {
  id: string;
  role: string;
  [key: string]: any;
}

/** accountable is a single user ID string, not an array */
const isUserAccountable = (task: Task, userId: string): boolean =>
  task.raci?.accountable === userId;

/**
 * Check if user can perform work actions on a task (status changes)
 * Allowed: Admin/Manager OR Creator OR Responsible OR Accountable
 */
export const canDoWork = (task: Task, user: User | null): boolean => {
  if (!user) return false;

  const effectiveRole = normalizeRole(user.role);
  if (isAdminOrManager(effectiveRole)) return true;

  const userId = user.id;
  const isCreator = task.createdBy === userId;
  const isResponsible = task.raci?.responsible?.includes(userId) || false;
  const isAccountable = isUserAccountable(task, userId);

  return isCreator || isResponsible || isAccountable;
};

/**
 * Check if user can use timer on a task
 * Rules differ by context:
 * - myTasks: Only Responsible users on Responsible tab
 * - projectTasks: Any Responsible user
 */
export const canUseTimer = (
  task: Task,
  user: User | null,
  context: TaskContextType,
  currentRaciRole?: 'responsible' | 'accountable' | 'consulted' | 'informed'
): boolean => {
  if (!user) return false;

  const isResponsible = task.raci?.responsible?.includes(user.id) || false;

  if (context === 'myTasks') {
    return currentRaciRole === 'responsible' && isResponsible;
  }

  return isResponsible;
};

/**
 * Check if user can complete a task
 * Allowed: current Responsible only (Accountable / Consulted / Informed / admin-only cannot)
 */
export const canComplete = (task: Task, user: User | null): boolean => {
  if (!user) return false;
  return task.raci?.responsible?.includes(user.id) || false;
};

/**
 * Check if user can delete a task
 * Allowed: Admin/Manager OR Engineer who is creator or accountable
 */
export const canDelete = (task: Task, user: User | null): boolean => {
  if (!user) return false;
  if (task.status === TaskStatus.COMPLETED) return false;
  if (hasLoggedTimeBlockingDelete(Number(task.actualMinutes) || 0)) return false;

  const effectiveRole = normalizeRole(user.role);
  if (isAdminOrManager(effectiveRole)) return true;

  const isCreator = task.createdBy === user.id;
  const isAccountable = isUserAccountable(task, user.id);
  return effectiveRole === 'engineer' && (isCreator || isAccountable);
};

/**
 * Check if user can edit a task
 * Allowed: Admin/Manager OR Creator OR Responsible OR Accountable
 */
export const canEdit = (task: Task, user: User | null): boolean => {
  if (!user) return false;

  const effectiveRole = normalizeRole(user.role);
  if (isAdminOrManager(effectiveRole)) return true;

  const userId = user.id;
  const isCreator = task.createdBy === userId;
  const isResponsible = task.raci?.responsible?.includes(userId) || false;
  const isAccountable = isUserAccountable(task, userId);

  return isCreator || isResponsible || isAccountable;
};

/**
 * Whether the completion checkbox should be interactive for this task state
 */
export const canToggleCheckbox = (task: Task, user: User | null): boolean => {
  if (!user) return false;
  if (task.status === TaskStatus.COMPLETED) {
    return canDoWork(task, user);
  }
  return canComplete(task, user);
};

/**
 * Get all available permissions for a task
 */
export const getTaskPermissions = (
  task: Task,
  user: User | null,
  context: TaskContextType,
  currentRaciRole?: 'responsible' | 'accountable' | 'consulted' | 'informed'
): TaskTablePermissions => {
  return {
    canDoWork: canDoWork(task, user),
    canUseTimer: canUseTimer(task, user, context, currentRaciRole),
    canComplete: canComplete(task, user),
    canDelete: canDelete(task, user),
    canEdit: canEdit(task, user),
    canToggleCheckbox: canToggleCheckbox(task, user),
  };
};

/**
 * Get available actions for a task based on permissions and config
 */
export const getAvailableActions = (
  task: Task,
  user: User | null,
  context: TaskContextType,
  config: {
    enableCompletion: boolean;
    enableDeletion: boolean;
    enableTimer: boolean;
  },
  currentRaciRole?: 'responsible' | 'accountable' | 'consulted' | 'informed'
): {
  showTimer: boolean;
  showComplete: boolean;
  showDelete: boolean;
  showEdit: boolean;
} => {
  const permissions = getTaskPermissions(task, user, context, currentRaciRole);

  return {
    showTimer: config.enableTimer && permissions.canUseTimer,
    showComplete: config.enableCompletion && permissions.canComplete,
    showDelete: config.enableDeletion && permissions.canDelete,
    showEdit: permissions.canEdit,
  };
};
